import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ContentNavigationGroup } from "@appcaire/ui";
import { ContentSidebar } from "./ContentSidebar";
import { getLocalizedPath } from "@/utils/routes";
import { getBlogResourceCategories } from "@/pages/Resurser/resourceGroups";
import { ARTICLES, NEWS_ARTICLES } from "@/pages/Resurser/articlesRegistry";
import { GUIDES } from "@/pages/Resurser/guidesRegistry";
import { COMPARISONS } from "@/pages/Resurser/comparisonsRegistry";

/** One editorial tree remains available across indexes and independent SEO articles. */
export function EditorialNavigationLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const { t } = useTranslation("resources");
  const locale = pathname.startsWith("/en/") ? "en" : "sv";
  const categories = getBlogResourceCategories(locale === "en");
  const dateFormat = new Intl.DateTimeFormat(
    locale === "en" ? "en-GB" : "sv-SE",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    },
  );
  const active = categories.some((category) => {
    const path = getLocalizedPath(category.routeKey, locale);
    return pathname === path || pathname.startsWith(`${path}/`);
  });
  if (
    !active &&
    pathname !== getLocalizedPath("resourcesCategory.blog", locale)
  )
    return <>{children}</>;
  const inventories = [
    ARTICLES.filter((article) => article.kind !== "news"),
    GUIDES,
    COMPARISONS,
    NEWS_ARTICLES,
  ];
  return (
    <div className="mx-auto grid max-w-screen-2xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-4 lg:px-8">
      <div className="min-w-0 lg:col-span-1">
        <ContentNavigationGroup>
          <ContentSidebar
            title={
              locale === "en" ? "Knowledge & resources" : "Kunskap & resurser"
            }
            items={categories.map((category, index) => {
              const path = getLocalizedPath(category.routeKey, locale);
              return {
                href: path,
                label: category.title,
                icon: category.icon,
                domain: category.domain,
                defaultExpanded:
                  pathname === path ||
                  pathname.startsWith(`${path}/`) ||
                  (!active && index === 0),
                children: [
                  {
                    href: path,
                    label:
                      locale === "en"
                        ? `All ${category.title.toLowerCase()}`
                        : `Alla ${category.title.toLowerCase()}`,
                    current: pathname === path ? ("page" as const) : undefined,
                  },
                  ...[...inventories[index]]
                    .sort((a, b) => b.publishDate.localeCompare(a.publishDate))
                    .map((entry) => {
                      const href = getLocalizedPath(entry.routeKey, locale);
                      return {
                        href,
                        label: String(t(entry.title.key, entry.title.fallback)),
                        badge:
                          "category" in entry
                            ? String(
                                t(entry.category.key, entry.category.fallback),
                              )
                            : category.title,
                        date: {
                          value: entry.publishDate,
                          label: dateFormat.format(
                            new Date(`${entry.publishDate}T00:00:00Z`),
                          ),
                        },
                        current:
                          pathname === href ? ("page" as const) : undefined,
                      };
                    }),
                ],
              };
            })}
          />
        </ContentNavigationGroup>
      </div>
      <div className="min-w-0 lg:col-span-3">{children}</div>
    </div>
  );
}
